/**
 * AI Safety Incidents — curated catalogue of historic AI failures, jailbreaks,
 * data leaks, and alignment scares. Sourced from /api/frontier/incidents.
 * News feed below pulls related articles from the last N days.
 */
import { useEffect, useMemo, useState } from 'react'
import { Skull, RefreshCw, ExternalLink, Filter } from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'
import Sparkline from '../components/Sparkline'

interface Incident {
  id: string
  title: string
  year: number
  date?: string  // YYYY-MM-DD when known
  category: string
  lab: string
  severity: string
  summary: string
  url: string
}

interface NewsItem {
  id: number | string
  title: string
  summary: string
  source: string
  url: string
  category: string
  published_at: string | null
}

const SEV_TONE: Record<string, string> = {
  high:   'bg-rose/20 text-rose border-rose/40',
  medium: 'bg-amber/15 text-amber-light border-amber/40',
  low:    'bg-blue/15 text-blue-light border-blue/40',
}

const CAT_TONE: Record<string, string> = {
  alignment:        'bg-rose/15 text-rose-light',
  jailbreak:        'bg-amber/15 text-amber-light',
  'prompt-injection':'bg-amber/15 text-amber-light',
  hallucination:    'bg-blue/15 text-blue-light',
  'data-leak':      'bg-purple/15 text-purple-light',
  'weight-leak':    'bg-purple/15 text-purple-light',
}

export default function AIIncidentsPage() {
  const [incidents, setIncidents] = useState<Incident[]>([])
  const [categories, setCategories] = useState<string[]>([])
  const [news, setNews] = useState<NewsItem[]>([])
  const [catFilter, setCatFilter] = useState<string>('')
  const [sevFilter, setSevFilter] = useState<string>('')
  const [loading, setLoading] = useState(false)

  const refresh = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/frontier/incidents').then(r => {
        setIncidents(r.data?.incidents || [])
        setCategories(r.data?.categories || [])
      }).catch(() => {}),
      api.get('/api/frontier/incidents/news?limit=30&days=120').then(r => {
        setNews(r.data?.articles || [])
      }).catch(() => {}),
    ]).finally(() => setLoading(false))
  }
  useEffect(() => { refresh() }, [])

  const filtered = useMemo(() => incidents.filter(i =>
    (!catFilter || i.category === catFilter) &&
    (!sevFilter || i.severity === sevFilter)
  ), [incidents, catFilter, sevFilter])

  const counts = useMemo(() => ({
    high:   incidents.filter(i => i.severity === 'high').length,
    medium: incidents.filter(i => i.severity === 'medium').length,
    low:    incidents.filter(i => i.severity === 'low').length,
  }), [incidents])

  // Year-bucketed series for sparklines — fills missing years with 0
  const byYearSeries = useMemo(() => {
    if (incidents.length === 0) return { all: [], high: [] }
    const minY = Math.min(...incidents.map(i => i.year))
    const maxY = Math.max(...incidents.map(i => i.year))
    const all: { t: string; v: number }[] = []
    const high: { t: string; v: number }[] = []
    for (let y = minY; y <= maxY; y++) {
      all.push({ t: String(y), v: incidents.filter(i => i.year === y).length })
      high.push({ t: String(y), v: incidents.filter(i => i.year === y && i.severity === 'high').length })
    }
    return { all, high }
  }, [incidents])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <Skull size={18} className="text-rose-light" /> AI SAFETY INCIDENTS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          curated · historic failures · what we learned
        </span>
        <button type="button" onClick={refresh} disabled={loading} className="btn-ghost ml-auto">
          <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Tile label="Total"     value={incidents.length} tone="blue"
              spark={byYearSeries.all}  sparkTone="rose" />
        <Tile label="High sev"  value={counts.high}      tone="rose"
              spark={byYearSeries.high} sparkTone="rose" />
        <Tile label="Medium"    value={counts.medium}    tone="amber" />
        <Tile label="Low"       value={counts.low}       tone="emerald" />
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        <Filter size={12} className="text-text-muted" />
        <select className="input" value={catFilter} onChange={e => setCatFilter(e.target.value)} aria-label="Filter by category">
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="input" value={sevFilter} onChange={e => setSevFilter(e.target.value)} aria-label="Filter by severity">
          <option value="">All severities</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
        <span className="text-text-muted ml-auto">{filtered.length} of {incidents.length} incidents</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {filtered.map(i => (
          <div key={i.id} className="card p-4">
            <div className="flex items-start gap-2 mb-2">
              <span className="text-[11px] font-mono text-amber-light font-semibold mt-0.5 shrink-0">
                {i.date || String(i.year)}
              </span>
              <h3 className="text-sm font-semibold text-text-primary leading-snug flex-1">{i.title}</h3>
              <span className={clsx('badge border shrink-0', SEV_TONE[i.severity] || 'bg-surface-raised text-text-muted border-border')}>
                {i.severity}
              </span>
            </div>
            <div className="flex flex-wrap gap-1 mb-2">
              <span className={clsx('badge text-[9px]', CAT_TONE[i.category] || 'bg-surface-raised text-text-muted')}>{i.category}</span>
              <span className="badge text-[9px] bg-surface-raised text-text-muted">{i.lab}</span>
            </div>
            <p className="text-xs text-text-secondary leading-relaxed">{i.summary}</p>
            <a href={i.url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[11px] text-blue-light hover:underline mt-2">
              Reference <ExternalLink size={10} />
            </a>
          </div>
        ))}
      </div>

      {news.length > 0 && (
        <section className="pt-3 mt-3 border-t border-border">
          <h2 className="text-[10px] uppercase tracking-[0.3em] text-text-muted mb-2">
            Related coverage · last 120 days
          </h2>
          <ul className="space-y-1.5">
            {news.map(n => (
              <li key={n.id} className="flex items-baseline gap-2 text-xs">
                <span className="text-[10px] text-text-muted font-mono shrink-0">
                  {n.published_at ? n.published_at.slice(0, 10) : '—'}
                </span>
                <span className="text-[10px] text-text-muted shrink-0">{n.source}</span>
                <a href={n.url} target="_blank" rel="noreferrer"
                  className="text-text-secondary hover:text-blue-light truncate">
                  {n.title}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  )
}

function Tile({ label, value, tone, spark, sparkTone }: {
  label: string; value: number;
  tone: 'rose' | 'emerald' | 'amber' | 'blue';
  spark?: { t: string; v: number }[];
  sparkTone?: 'rose' | 'emerald' | 'amber' | 'blue';
}) {
  const c = tone === 'rose' ? 'text-rose' : tone === 'emerald' ? 'text-emerald' : tone === 'amber' ? 'text-amber-light' : 'text-blue-light'
  return (
    <div className="card p-3">
      <div className="flex items-baseline gap-2">
        <div className="text-[10px] uppercase tracking-widest text-text-muted">{label}</div>
        {spark && spark.length >= 2 && (
          <Sparkline points={spark} tone={sparkTone || 'auto'} width={50} height={14} className="ml-auto" />
        )}
      </div>
      <div className={clsx('text-2xl font-black font-mono mt-1', c)}>{value.toLocaleString()}</div>
    </div>
  )
}
